import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
});
const pending = new Map();
const getKey = (config) => {
  const { method, url, params, data } = config;
  return [method, url, JSON.stringify(params || {}), JSON.stringify(data || {})].join('&');
};

const addPending = (config) => {
  const key = getKey(config);
  if (pending.has(key)) {
    const cancel = pending.get(key);
    cancel('duplicate');
  }
  config.cancelToken = new axios.CancelToken((c) => pending.set(key, c));
};

const removePending = (config) => {
  const key = getKey(config);
  if (pending.has(key)) {
    pending.delete(key);
  }
};

api.interceptors.request.use((config) => {
  addPending(config);
  return config;
});

api.interceptors.response.use(
  (response) => {
    removePending(response.config);
    return response;
  },
  (error) => {
    if (error.config) removePending(error.config);
    return Promise.reject(error);
  }
);
// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Don't redirect here - let the AuthContext handle the state change
      // This prevents infinite reload loops
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
  logout: () => api.post('/auth/logout'),
};

// Customer API
export const customerAPI = {
  getCustomers: (params) => api.get('/customers', { params }),
  getCustomer: (id) => api.get(`/customers/${id}`),
  createCustomer: (customerData) => api.post('/customers', customerData),
  updateCustomer: (id, customerData) => api.put(`/customers/${id}`, customerData),
  deleteCustomer: (id) => api.delete(`/customers/${id}`),
  searchCustomers: (searchData) => api.get('/customers/search', { params: searchData }),
  uploadPhoto: (id, formData) => api.post(`/customers/${id}/upload-photo`, formData),
};

// Loan API
export const loanAPI = {
  getLoans: (params) => api.get('/loans', { params }),
  getLoan: (id) => api.get(`/loans/${id}`),
  createLoan: (loanData) => api.post('/loans', loanData),
  updateLoan: (id, loanData) => api.put(`/loans/${id}`, loanData),
  closeLoan: (id, data) => api.post(`/loans/${id}/close`, data),
  calculateInterest: (id, params) => api.get(`/loans/${id}/calculate-interest`, { params }),
};

// Payment API
export const paymentAPI = {
  getPayments: (params) => api.get('/payments', { params }),
  getPayment: (id) => api.get(`/payments/${id}`),
  getLoanPayments: (loanId) => api.get(`/payments/loan/${loanId}`),
  createPayment: (paymentData) => api.post('/payments', paymentData),
  generateReceipt: (id) => api.post(`/payments/${id}/receipt`),
};

// Report API
export const reportAPI = {
  getCustomerLedger: (customerId, params) => 
    api.get(`/reports/customer-ledger/${customerId}`, { params }),
  getOutstandingLoans: (params) => api.get('/reports/outstanding-loans', { params }),
  getDefaulterList: (params) => api.get('/reports/defaulter-list', { params }),
  getBusinessSummary: (params) => api.get('/reports/business-summary', { params }),
  getInterestReport: (params) => api.get('/reports/interest-report', { params }),
  getItemAnalysis: (params) => api.get('/reports/item-analysis', { params }),
  getDashboardSnapshot: () => api.get('/reports/dashboard-snapshot'),
  calculateAllInterest: () => api.post('/reports/calculate-all-interest'),
};

// Items API
export const itemsAPI = {
  getCategories: () => api.get('/items/categories'),
};

// Settings API
export const settingsAPI = {
  getMetalRates: () => api.get('/settings/metal-rates'),
  updateMetalRates: (data) => api.put('/settings/metal-rates', data),
  syncMetalRates: () => api.post('/settings/metal-rates/sync'),
};

// User API
export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  updateUserStatus: (id, data) => api.put(`/users/${id}/status`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  getUserActivity: (id, params) => api.get(`/users/${id}/activity`, { params }),
};

// Utility functions
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (date) => {
  return new Date(date).toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{5})(\d{5})$/);
  if (match) {
    return `${match[1]} ${match[2]}`;
  }
  return phone;
};

export const generatePDF = async (elementId, filename) => {
  const { default: html2canvas } = await import('html2canvas');
  const { default: jsPDF } = await import('jspdf');
  
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Element not found');
  }

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF('p', 'mm', 'a4');
  
  const imgWidth = 210;
  const pageHeight = 295;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;

  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft >= 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(filename);
};

export default api;
